import { defineStore } from 'pinia';
import { LAST_VIEWED_DASHBOARD, readHubPosition, visitedConnection, writeHubPosition } from '../utils/hubPositions';
import { ref, computed } from 'vue';
import { apiFetch } from '../services/api';
import { useSocketStore } from './socket';
import { useEntitiesStore } from './entities';
import { useAuthStore } from './auth';
import { useBimModelStore } from './bimModel';
import { disposeAllBimScenes } from '../composables/useBimScene';
import { useBreakpoints } from '@vueuse/core';
import { deserializeDashboard, deserializeWidget } from '../utils/serializers';
import { getWidgetConstraints, type WidgetType } from '@/constants/widgetConstraints';
import type { Dashboard, DashboardWidget } from '@/types';
import { widgetConfigOf, type DashboardWidgetLike } from '../components/dashboard/dashboardTypes';
import { isLayoutDashboard, layoutHeroCategories, layoutHeroOrder } from '../utils/layoutMarker';
import { pickMobileDefault } from '../utils/mobileDashboard';
import { isSystemPageRow, systemPageKindOfRow } from '../utils/systemPages';
import type { JsonValue } from '@hub/types/json';
import { wireNumber, wireString, wireStringArray, type WireJsonBag } from '../utils/wire';

export interface ActiveEditor {
  id: string;
  /** The editor's own name — the badge names them by it, else by the address below. */
  fullName: string | null;
  /** The address the editor is reached through, when they have no name. */
  email: string | null;
  widgetId: string;
  timestamp: number;
}

/**
 * A dashboard's layout config bag (columns/rows/displayMode/…).
 *
 * `Dashboard.layoutConfig` is a hub JSON column (`JsonValue`), so this view of
 * the same object is an **open bag of `JsonValue`** and nothing else: the keys
 * below are the ones this store reads or writes, and a key it does not name
 * stays a `JsonValue` a reading surface narrows (the fuller view the editors
 * keep is `components/dashboard/dashboardTypes.ts`). Declaring a key as
 * `unknown` here would be naming nothing the column can hold.
 */
export interface DashboardLayoutConfig extends WireJsonBag {
  columns?: number;
  rows?: number;
  displayMode?: string;
  rowHeight?: number;
  gap?: number;
  /** Ids of the widgets pinned into the layout's control strip. */
  layoutControls?: string[];
  /** Legacy control strip (superseded by `layoutControls`). */
  secondaryControls?: JsonValue;
}

/**
 * A widget's config bag — its shape depends on the widget type, so this store
 * carries it and reads only the placement fields (`components/dashboard/
 * dashboardTypes.ts` types its own fuller view of the same object).
 *
 * It extends the wire bag because the hub stores the value in a `Json` column
 * (`Widget.widgetConfig: JsonValue`): the declared fields are the ones this
 * store reads, and the index signature is what makes the bag — and the object
 * the editors build from it — assignable back into that column without a cast.
 */
export interface DashboardWidgetConfig extends WireJsonBag {
  title?: string;
  /** The device this control widget drives. */
  id?: string;
  /** How the picker widgets choose their devices. */
  maxDevices?: number;
  deviceIds?: string[];
  device_ids?: string[];
  deviceTypes?: string[];
  filterByCategory?: string;
  roomId?: string;
  /** Floor plan / BIM position and size, in canvas units. */
  x?: number;
  y?: number;
  w?: number;
  h?: number;
  z?: number;
  /** BIM 3D position. */
  x3d?: number;
  y3d?: number;
  z3d?: number;
  /** Grid / column layout slot. */
  col?: number;
  row?: number;
  colSpan?: number;
  rowSpan?: number;
  /** Stacking order of the control layouts. */
  order?: number;
  zIndex?: number;
}

/** The PUT body `/layouts/:id` accepts — the fields the editor can change. */
export interface DashboardUpdatePayload {
  name?: string;
  icon?: string | null;
  layoutType?: string;
  /**
   * The wire carries the column as it is stored: the hub types `layoutConfig`
   * `JsonValue`, so the body does too (the editors build the bag, and a body
   * built from a stored row passes that row's own object straight through).
   */
  layoutConfig?: JsonValue;
  cameraBackgroundId?: string | null;
  backgroundStreamType?: string | null;
  sortOrder?: number;
}

/**
 * One datapoint row a dashboard widget subscribes to, as it sits in
 * `dashboardData.datapoints` (deviceId → rows).
 *
 * Both keys of the same row are named because two readers use a different one
 * each and the handoff to the dashboard layouts declares the first: the widget
 * payloads are matched by the hub's own `datapoint_id` (`ChartCard`'s legacy
 * lookup, and `DashboardDatapointLike` in `components/dashboard/dashboardTypes`)
 * while the required-datapoint scan below reads the camelCase `datapointId`.
 * Every other field on the row stays `unknown` and is narrowed where it is read.
 */
export interface DashboardDatapoint {
  /** The hub's key for the row (what widget configs reference). */
  datapoint_id: string;
  /** The camelCase alias the required-datapoint scan reads. */
  datapointId: string;
  [key: string]: unknown;
}

export interface DashboardData {
  datapoints: Map<string, DashboardDatapoint[]>; // deviceId -> datapoints
}



export const useDashboardStore = defineStore('dashboard', () => {
  const socketStore = useSocketStore();
  const entitiesStore = useEntitiesStore();

  // Breakpoint detection for mobile
  const breakpoints = useBreakpoints({
    mobile: 0,
    tablet: 768,
    desktop: 1024,
  });
  const isMobile = breakpoints.smaller('tablet');

  // State
  const dashboards = ref<Dashboard[]>([]);
  const activeDashboardId = ref<string | null>(null);
  const widgets = ref<DashboardWidgetLike[]>([]);
  const editMode = ref(false);
  const loading = ref(false);
  const error = ref<string | null>(null);

  // Active editors tracking
  const activeEditors = ref<Map<string, ActiveEditor[]>>(new Map());

  // Centralized data cache (only datapoints, others from entities store)
  const dashboardData = ref<DashboardData>({
    datapoints: new Map(),
  });
  const dataLoaded = ref(false);
  const visibleWidgets = ref<Set<string>>(new Set());
  const subscribedDatapoints = ref<Set<string>>(new Set());

  // Zoom/pan state persistence (client-side only, per dashboard)
  const zoomStates = ref<Map<string, { scale: number; offsetX: number; offsetY: number }>>(new Map());

  // Computed
  const activeDashboard = computed(
    () =>
      dashboards.value.find((d) => d.id === activeDashboardId.value) ??
      systemPages.value.find((d) => d.id === activeDashboardId.value) ??
      null,
  );

  /**
   * The rows that are layouts rather than dashboards. Same table, same editors:
   * the marker in `layoutConfig.layout` is the whole difference
   * (`utils/layoutMarker.ts`).
   */
  const layouts = computed(() => dashboards.value.filter((row) => isLayoutDashboard(row)));

  /**
   * The app's two own pages (`utils/systemPages.ts`): the dashboard and the hub's
   * 3D house.
   *
   * They are layout ROWS by storage — the dashboard's cards are widgets, the 3D
   * page's presets are viewpoints — but they are not layouts, and the hub no
   * longer lists them as such: `/api/layouts` answers with layouts only, and
   * these two come from `/api/system/pages`. So a list cannot forget to skip
   * them (owner, 2026-10-31: *"we wanted to deattach dashboard and 3d model pages
   * from Layouts!!"*).
   */
  const systemPages = ref<Dashboard[]>([]);

  const modelPage = computed(
    () => systemPages.value.find((row) => systemPageKindOfRow(row) === 'model') ?? null,
  );

  /**
   * The page a phone opens (`utils/mobileDashboard.ts`): the column page the hub
   * keeps for narrow screens, or null on a hub that has none yet.
   */
  const mobileDefaultDashboard = computed(
    () => pickMobileDefault(dashboards.value, isLayoutDashboard) ?? null,
  );

  /** The layouts that are a hero for `category`, in slide order. */
  function heroLayoutsFor(category: string): Dashboard[] {
    return layouts.value
      .filter((row) => layoutHeroCategories(row.layoutConfig).includes(category))
      .sort((left, right) => {
        const order = layoutHeroOrder(left.layoutConfig) - layoutHeroOrder(right.layoutConfig);
        return order !== 0 ? order : left.name.localeCompare(right.name);
      });
  }

  const canEdit = computed(() => !isMobile.value);

  const sortedWidgets = computed(() => {
    return [...widgets.value].sort((a, b) => {
      if (a.positionY !== b.positionY) {
        return a.positionY - b.positionY;
      }
      return a.positionX - b.positionX;
    });
  });

  /**
   * The hub's own pages, in menu order: everything that is a page rather than a
   * layout. The template-backed "primary" dashboards are gone with the
   * templates, so this is the one list a menu or a picker has to walk — and the
   * dashboard and the 3D house are not on it, because the app owns those two.
   */
  const customDashboards = computed(() =>
    dashboards.value.filter((dashboard) => !isLayoutDashboard(dashboard) && !isSystemPageRow(dashboard)),
  );

  /**
   * Does this hub already have its 3D page? `t_hub_bim_model` allows exactly one,
   * shared by every account, so the create/edit surfaces offer the BIM layout only
   * while this is false (and the hub refuses a second one either way).
   */
  const hasHubBimDashboard = computed(() => modelPage.value !== null);

  const pageNavigationDashboards = computed(() =>
    dashboards.value.filter((dashboard) => !isLayoutDashboard(dashboard) && !isSystemPageRow(dashboard)),
  );

  const bottomMenuDashboards = computed(() => {
    const active = activeDashboard.value;

    if (!active) {
      return [] as Dashboard[];
    }

    // The switcher lists the pages an owner made. The dashboard and the 3D
    // house are the APP's two surfaces: they are reached from the sidebar and
    // each answers at its own path, so a tab for them would be a second door —
    // and the one the owner kept finding (2026-10-31: *"why is it still under
    // layouts"*).
    return dashboards.value
      .filter((dashboard) => !isLayoutDashboard(dashboard) && !isSystemPageRow(dashboard))
      .sort((left, right) => {
        if (left.sortOrder !== right.sortOrder) return left.sortOrder - right.sortOrder;
        return (left.name ?? '').localeCompare(right.name ?? '');
      });
  });

  // Actions

  /**
   * Load all user dashboards.
   *
   * `routeHubId` is the hub a hub-scoped route names (`/h/:hubId/...`), when the
   * caller knows it. The remembered dashboard is READ for exactly that
   * connection (`utils/hubPositions.ts`); without it the position belongs to the
   * endpoint this client is pointed at, which is the same connection on a
   * LAN/Electron route.
   */
  async function loadDashboards(routeHubId?: string | string[] | null) {
    loading.value = true;
    error.value = null;
    try {
      const data = await apiFetch('/layouts');
      if (!Array.isArray(data)) {
        console.error('[DashboardStore] /dashboards returned non-array', typeof data);
        dashboards.value = [];
        return;
      }
      dashboards.value = data.map(deserializeDashboard);

      // The app's two own pages, from their own endpoint. A hub that predates
      // `/api/system/pages` simply has none — the routes fall back to their
      // empty states rather than failing this load.
      try {
        const pages = await apiFetch('/system/pages');
        const rows = [pages?.dashboard, pages?.model].filter(Boolean) as unknown[];
        systemPages.value = rows.map((row) => deserializeDashboard(row));
      } catch (systemPagesError) {
        console.warn('[DashboardStore] /system/pages unavailable', systemPagesError);
        systemPages.value = [];
      }

      // Set active dashboard from this connection's own position, else the
      // hub's default, else the first. The position used to be read through
      // `getHubSessionKeys()`, which deliberately also yields
      // `otto_active_hub_key` — the hub being LEFT — so another hub's dashboard
      // id could be picked up here (card t_251ff2eb); the router's dashboard
      // reader (`src/router/index.ts`, `_redirect`) reads the same key list.
      // No bare, hub-less `lastViewedDashboard` fallback either: nothing writes
      // a hub-less position any more, so such a value could only be one no
      // reader owns — a leftover from a build before this rule.
      if (!activeDashboardId.value && dashboards.value.length > 0) {
        const lastViewedId = readHubPosition(LAST_VIEWED_DASHBOARD, visitedConnection(routeHubId));
        // A layout is never the page a hub opens: it is a surface a dashboard
        // or a category page places, so the pick below never lands on one.
        const openable = dashboards.value.filter((row) => !isLayoutDashboard(row));
        let targetDashboard = null;

        if (lastViewedId) {
          targetDashboard = openable.find((d) => d.id === lastViewedId);
        }

        if (!targetDashboard) {
          const defaultDashboard = openable.find((d) => d.isDefault);
          targetDashboard = defaultDashboard || openable[0];
        }

        if (targetDashboard) {
          activeDashboardId.value = targetDashboard.id;
        }
      }
    } catch (__caught: unknown) {
      const err = __caught as {
        response?: {
          status?: number;
          data?: { error?: string; message?: string; details?: string; [k: string]: unknown };
        };
      };
      error.value = err.response?.data?.error || 'Failed to load dashboards';
      console.error('Error loading dashboards:', err);
    } finally {
      loading.value = false;
    }
  }

  

  /**
   * Load widgets for active dashboard
   */
  async function loadWidgets(dashboardId: string) {
    loading.value = true;
    error.value = null;
    try {
      const data = await apiFetch(`/layouts/${dashboardId}`);
      widgets.value = (data.widgets || []).map(deserializeWidget);
    } catch (__caught: unknown) {
      const err = __caught as {
        response?: {
          status?: number;
          data?: { error?: string; message?: string; details?: string; [k: string]: unknown };
        };
      };
      error.value = err.response?.data?.error || 'Failed to load widgets';
      console.error('Error loading widgets:', err);
    } finally {
      loading.value = false;
    }
  }

  /**
   * Load datapoints for a specific device (cached)
   */
  async function loadDeviceDatapoints(deviceId: string) {
    if (dashboardData.value.datapoints.has(deviceId)) {
      return dashboardData.value.datapoints.get(deviceId);
    }

    try {
      const data = await apiFetch(`/devices/${deviceId}/datapoints`);
      dashboardData.value.datapoints.set(deviceId, data);
      return data;
    } catch (err) {
      console.error(`Error loading datapoints for device ${deviceId}:`, err);
      return [];
    }
  }

  /**
   * Mark widget as visible (for subscription management)
   */
  function markWidgetVisible(id: string) {
    visibleWidgets.value.add(id);
    updateDatapointSubscriptions();
  }

  /**
   * Mark widget as hidden
   */
  function markWidgetHidden(id: string) {
    visibleWidgets.value.delete(id);
    updateDatapointSubscriptions();
  }

  /**
   * Update WebSocket subscriptions based on visible widgets
   */
  function updateDatapointSubscriptions() {
    const requiredDatapoints = new Set<string>();

    // Collect datapoints needed by visible widgets
    widgets.value.forEach((widget) => {
      if (!visibleWidgets.value.has(widget.id)) return;

      // Extract datapoint IDs from widget config
      const config = widget.widgetConfig;

      const deviceId = wireString(config.deviceId);
      if (deviceId) {
        // Subscribe to all datapoints for this device
        const device = entitiesStore.getDevice(deviceId);
        if (device) {
          const datapoints = dashboardData.value.datapoints.get(deviceId);
          datapoints?.forEach((dp) => {
            requiredDatapoints.add(dp.datapointId);
          });
        }
      }

      for (const id of wireStringArray(config.deviceIds) ?? []) {
        const datapoints = dashboardData.value.datapoints.get(id);
        datapoints?.forEach((dp) => {
          requiredDatapoints.add(dp.datapointId);
        });
      }

      for (const dpId of wireStringArray(config.datapointIds) ?? []) {
        requiredDatapoints.add(dpId);
      }
    });

    // Unsubscribe from datapoints no longer needed
    subscribedDatapoints.value.forEach((dpId) => {
      if (!requiredDatapoints.has(dpId)) {
        // Unsubscribe logic here (if needed)
        subscribedDatapoints.value.delete(dpId);
      }
    });

    // Subscribe to new datapoints
    requiredDatapoints.forEach((dpId) => {
      if (!subscribedDatapoints.value.has(dpId)) {
        // Subscribe logic here (handled by socket store)
        subscribedDatapoints.value.add(dpId);
      }
    });
  }

  /**
   * Set active dashboard
   */
  async function setActiveDashboard(dashboardId: string, routeHubId?: string | string[] | null) {
    activeDashboardId.value = dashboardId;
    // Persist for the next visit — for exactly ONE connection: the hub a
    // hub-scoped route names, else the endpoint this client is pointed at
    // (`utils/hubPositions.ts`). This used to walk `getHubSessionKeys()`, which
    // deliberately also yields `otto_active_hub_key` (the hub being LEFT) and
    // every spelling of the current endpoint, so one dashboard visit wrote the
    // id under another hub's key as well — measured on the tablet: a single
    // visit to `…/dashboards/<id>` left `lastViewedDashboard:hub:517e7adb-…`
    // next to both `local:torik.ai/api` spellings (card t_251ff2eb).
    writeHubPosition(LAST_VIEWED_DASHBOARD, dashboardId, visitedConnection(routeHubId));
    await loadWidgets(dashboardId);

    // Join dashboard room for collaboration
    joinDashboardRoom(dashboardId);
  }

  /**
   * Clone dashboard from template
   */
  /**
   * Create new custom dashboard
   */
  async function createDashboard(
    name: string,
    layoutType: 'layout' | 'column' | 'board' | 'overview' | 'grid' | 'floorplan' | 'scada' | 'bim' = 'board',
    icon?: string,
    options?: { layoutConfig?: DashboardLayoutConfig; basePlanId?: string | null },
    routeHubId?: string | string[] | null,
  ) {
    loading.value = true;
    error.value = null;
    try {
      const sharedLayoutDefaults = {
        displayMode: 'layout-only',
        belowColumns: 2,
        aspectRatioPresetId: 'widescreen-16-9',
      };
      const bimLayoutDefaults = {
        displayMode: 'layout-only',
        belowColumns: 2,
      };
      const layoutConfig =
        options?.layoutConfig ??
        (layoutType === 'grid'
          ? { ...sharedLayoutDefaults, columns: 12, rows: 4 }
          : layoutType === 'column'
            ? { columns: 4 }
            : layoutType === 'layout' || layoutType === 'floorplan' || layoutType === 'scada'
              ? { ...sharedLayoutDefaults }
              : layoutType === 'bim'
                ? { ...bimLayoutDefaults }
                : {});

      const newDashboard = await apiFetch('/layouts', {
        method: 'POST',
        body: JSON.stringify({
          name,
          layoutType: layoutType,
          layoutConfig,
          icon: icon || null,
          basePlanId: options?.basePlanId ?? null,
        }),
      });
      const normalizedDashboard = deserializeDashboard(newDashboard);
      dashboards.value.push(normalizedDashboard);
      await setActiveDashboard(normalizedDashboard.id, routeHubId);
      return normalizedDashboard;
    } catch (__caught: unknown) {
      const err = __caught as {
        response?: {
          status?: number;
          data?: { error?: string; message?: string; details?: string; [k: string]: unknown };
        };
      };
      error.value = err.response?.data?.error || 'Failed to create dashboard';
      console.error('Error creating dashboard:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Update dashboard
   */
  async function updateDashboard(dashboardId: string, updates: Partial<Dashboard>) {
    try {
      // Build payload - API expects camelCase
      const payload: DashboardUpdatePayload = {};
      if (updates.name !== undefined) payload.name = updates.name;
      if (updates.icon !== undefined) payload.icon = updates.icon;
      if (updates.layoutType !== undefined) payload.layoutType = updates.layoutType;
      if (updates.layoutConfig !== undefined) payload.layoutConfig = updates.layoutConfig;
      if (updates.cameraBackgroundId !== undefined) payload.cameraBackgroundId = updates.cameraBackgroundId;
      if (updates.backgroundStreamType !== undefined) payload.backgroundStreamType = updates.backgroundStreamType;
      if (updates.sortOrder !== undefined) payload.sortOrder = updates.sortOrder;
  
      const response = await apiFetch(`/layouts/${dashboardId}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      const index = dashboards.value.findIndex((d) => d.id === dashboardId);
      if (index !== -1) {
        dashboards.value[index] = deserializeDashboard(response);
      }

      // Broadcast layout change if layout updated. A config-only write (the side
      // drawer, the control strip) still carries the view's CURRENT layout type,
      // and a type-only write carries the current config — an `undefined` on
      // either side made every other open screen drop what it was showing.
      if (updates.layoutType || updates.layoutConfig) {
        const updated = index !== -1 ? dashboards.value[index] : activeDashboard.value;
        if (updated) {
          broadcastLayoutChange(
            dashboardId,
            updates.layoutType ?? updated.layoutType,
            updates.layoutConfig ?? updated.layoutConfig,
          );
        }
      }
    } catch (__caught: unknown) {
      const err = __caught as {
        response?: {
          status?: number;
          data?: { error?: string; message?: string; details?: string; [k: string]: unknown };
        };
      };
      error.value = err.response?.data?.error || 'Failed to update dashboard';
      console.error('Error updating dashboard:', err);
      throw err;
    }
  }

  /**
   * Delete dashboard
   */
  async function deleteDashboard(dashboardId: string) {
    try {
      await apiFetch(`/layouts/${dashboardId}`, { method: 'DELETE' });
      dashboards.value = dashboards.value.filter((d) => d.id !== dashboardId);

      if (activeDashboardId.value === dashboardId) {
        activeDashboardId.value = dashboards.value[0]?.id || null;
        if (activeDashboardId.value) {
          await loadWidgets(activeDashboardId.value);
        }
      }
    } catch (__caught: unknown) {
      const err = __caught as {
        response?: {
          status?: number;
          data?: { error?: string; message?: string; details?: string; [k: string]: unknown };
        };
      };
      error.value = err.response?.data?.error || 'Failed to delete dashboard';
      console.error('Error deleting dashboard:', err);
      throw err;
    }
  }

  /**
   * Reset a system dashboard page to its default preset
   */
  /**
   * Reorder dashboards (persist sortOrder)
   */
  async function reorderDashboards(dashboardIdsInOrder: string[]) {
    if (dashboardIdsInOrder.length === 0) {
      return;
    }

    const idSet = new Set(dashboardIdsInOrder);
    const orderById = new Map(dashboardIdsInOrder.map((id, index) => [id, index + 1]));

    const reorderedSubset = dashboardIdsInOrder
      .map((id) => dashboards.value.find((dashboard) => dashboard.id === id))
      .filter((dashboard): dashboard is Dashboard => Boolean(dashboard))
      .map((dashboard) => ({ ...dashboard, sortOrder: orderById.get(dashboard.id) ?? dashboard.sortOrder }));

    // Rebuild the store array so the reordered dashboards keep their new order in
    // the list. Previously only `sortOrder` was updated and the array order was
    // untouched, so the bottom menu (which renders the array order) snapped back
    // to the original order right after a drag.
    const rebuilt: Dashboard[] = [];
    let inserted = false;
    for (const dashboard of dashboards.value) {
      if (idSet.has(dashboard.id)) {
        if (!inserted) {
          rebuilt.push(...reorderedSubset);
          inserted = true;
        }
      } else {
        rebuilt.push(dashboard);
      }
    }
    dashboards.value = rebuilt;

    for (const [idx, id] of dashboardIdsInOrder.entries()) {
      if (!idSet.has(id)) {
        continue;
      }

      try {
        await updateDashboard(id, { sortOrder: idx + 1 });
      } catch (err) {
        console.error('Failed to update sort order for dashboard', id, err);
      }
    }
  }

  /** The plan canvas the hub draws on is 1920 × 1080; a widget with no spot lands in its middle. */
  const PLAN_CENTER = { x: 960, y: 540 };
  /** How far each further widget steps off a taken spot — a run of adds must not stack. */
  const PLAN_CASCADE_STEP = 28;

  /**
   * Where a widget lands on a plan: the middle, or stepped diagonally while that
   * spot is already taken. The editors' device picker stays open, so devices are
   * added one after another and would otherwise all land on the same pixel.
   */
  function planPlacement(x?: number, y?: number): { x: number; y: number } {
    const taken = new Set(
      widgets.value.map((widget) => {
        const config = widgetConfigOf(widget.widgetConfig);
        return `${wireNumber(config.x) ?? PLAN_CENTER.x}:${wireNumber(config.y) ?? PLAN_CENTER.y}`;
      }),
    );
    let cx = x ?? PLAN_CENTER.x;
    let cy = y ?? PLAN_CENTER.y;
    while (taken.has(`${cx}:${cy}`)) {
      cx += PLAN_CASCADE_STEP;
      cy += PLAN_CASCADE_STEP;
    }
    return { x: cx, y: cy };
  }

  /**
   * Add widget to dashboard
   */
  async function addWidget(
    widgetType: string,
    widgetConfig: DashboardWidgetConfig,
    position: { x: number; y: number },
    size: { colSpan: number; rowSpan: number },
  ) {
    if (!activeDashboardId.value) return;

    try {
      const dashboard = activeDashboard.value;
      let config = { ...widgetConfig };

      // A card is never a canvas object (see `utils/layoutDocument.ts`): on a
      // canvas it is a CONTROL below or beside the drawing. Only the retired
      // `floorplan` rows still position a widget in canvas pixels.
      if (dashboard?.layoutType === 'floorplan') {
        // Get widget-specific default sizes from constraints
        const constraints = getWidgetConstraints(widgetType as WidgetType, 'floorplan');
        const placement = planPlacement(widgetConfig.x, widgetConfig.y);

        config = {
          ...config,
          x: placement.x,
          y: placement.y,
          w: widgetConfig.w ?? constraints.defaultW,
          h: widgetConfig.h ?? constraints.defaultH,
        };
      } else {
        // For grid/column layouts, use col/row
        config = {
          ...config,
          col: position.x,
          row: position.y,
        };
      }

      const created = deserializeWidget(
        await apiFetch(`/layouts/${activeDashboardId.value}/widgets`, {
          method: 'POST',
          body: JSON.stringify({
            widget_type: widgetType,
            widget_config: config,
            position_x: position.x,
            position_y: position.y,
            col_span: size.colSpan,
            row_span: size.rowSpan,
          }),
        }),
      );

      widgets.value.push(created);
      return created;
    } catch (__caught: unknown) {
      const err = __caught as {
        response?: {
          status?: number;
          data?: { error?: string; message?: string; details?: string; [k: string]: unknown };
        };
      };
      error.value = err.response?.data?.error || 'Failed to add widget';
      console.error('Error adding widget:', err);
      throw err;
    }
  }

  /**
   * Update widget
   */
  async function updateWidget(id: string, updates: Partial<DashboardWidget>) {
    try {
      await apiFetch(`/layouts/widgets/${id}`, {
        method: 'PUT',
        body: JSON.stringify(updates),
      });

      const index = widgets.value.findIndex((w) => w.id === id);
      if (index !== -1) {
        const row = widgets.value[index];
        widgets.value[index] = {
          ...row,
          ...updates,
          // The wire and the store both carry the config column as JSON; here it
          // is the narrowed bag the layouts read (`undefined` in `updates` means
          // "leave the stored config alone" — it is not a write of nothing).
          widgetConfig: widgetConfigOf(updates.widgetConfig ?? row.widgetConfig),
        };
      }

      // Broadcast update
      if (updates.widgetConfig) {
        broadcastWidgetUpdate(id, updates.widgetConfig);
      }
    } catch (err) {
      console.error('Error updating widget:', err);
      throw err;
    }
  }

  /**
   * Move widget (update position)
   */
  async function moveWidget(id: string, x: number, y: number) {
    const widget = widgets.value.find((w) => w.id === id);
    if (!widget) return;

    // Optimistic update
    widget.positionX = x;
    widget.positionY = y;

    // Broadcast immediately
    broadcastWidgetMove(id, x, y);

    // Persist to server (debounced in real implementation)
    try {
      await apiFetch(`/layouts/widgets/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ positionX: x, positionY: y }),
      });
    } catch (err) {
      console.error('Error moving widget:', err);
    }
  }

  /**
   * Resize widget
   */
  async function resizeWidget(id: string, colSpan: number, rowSpan: number) {
    const widget = widgets.value.find((w) => w.id === id);
    if (!widget) return;

    // Optimistic update
    widget.colSpan = colSpan;
    widget.rowSpan = rowSpan;

    // Broadcast immediately
    broadcastWidgetResize(id, colSpan, rowSpan);

    // Persist to server
    try {
      await apiFetch(`/layouts/widgets/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ colSpan: colSpan, rowSpan: rowSpan }),
      });
    } catch (err) {
      console.error('Error resizing widget:', err);
    }
  }

  /**
   * Delete widget
   */
  async function deleteWidget(id: string) {
    try {
      await apiFetch(`/layouts/widgets/${id}`, { method: 'DELETE' });
      widgets.value = widgets.value.filter((w) => w.id !== id);

      // Broadcast deletion
      broadcastWidgetDelete(id);
    } catch (__caught: unknown) {
      const err = __caught as {
        response?: {
          status?: number;
          data?: { error?: string; message?: string; details?: string; [k: string]: unknown };
        };
      };
      error.value = err.response?.data?.error || 'Failed to delete widget';
      console.error('Error deleting widget:', err);
      throw err;
    }
  }

  /**
   * Toggle edit mode
   */
  function toggleEditMode() {
    if (!canEdit.value) return;
    editMode.value = !editMode.value;
  }

  // Collaboration functions

  /**
   * The signed-in person, as the dashboard collaboration events carry them
   * (card `t_33ae96b2`).
   *
   * These events used to carry `username` — the derived, invisible handle
   * (`users.username`, hub card `t_a8eb402a`) — read out of a `localStorage` key
   * (`'user'`) that nothing in this app writes. So the payload's name field was
   * `undefined`, and the badge on the other window painted the word where a
   * person belongs; had anything written that key, it would have painted
   * `anchor-retired-20260923214506 is editing` where the person is Tadej Savic.
   *
   * A person is named by their name, else the address they are reached through,
   * else a neutral word (`utils/personName.ts`), so the payload carries the two
   * fields that rule reads — never the handle — and the badge names the editor
   * with the same helper every other surface names people by. The identity is
   * the session's own (`stores/auth.ts`, `/users/me`), which is what the app
   * has; the dead `localStorage` key is not read any more.
   */
  function signedInPerson(): { id: string; fullName: string | null; email: string | null } {
    const me = useAuthStore().user;
    return {
      id: me?.id ?? '',
      fullName: me?.fullName ?? me?.name ?? null,
      email: me?.email ?? null,
    };
  }

  function joinDashboardRoom(dashboardId: string) {
    if (!socketStore.socket) return;

    const person = signedInPerson();
    socketStore.socket.emit('dashboard:join', {
      dashboardId: dashboardId,
      id: person.id,
      fullName: person.fullName,
      email: person.email,
    });
  }

  function startEditingWidget(id: string) {
    if (!activeDashboardId.value || !socketStore.socket) return;

    const person = signedInPerson();
    socketStore.socket.emit('dashboard:widget:editing:start', {
      dashboardId: activeDashboardId.value,
      widgetId: id,
      id: person.id,
      fullName: person.fullName,
      email: person.email,
    });
  }

  function stopEditingWidget(id: string) {
    if (!activeDashboardId.value || !socketStore.socket) return;

    const person = signedInPerson();
    socketStore.socket.emit('dashboard:widget:editing:stop', {
      dashboardId: activeDashboardId.value,
      widgetId: id,
      id: person.id,
    });
  }

  function broadcastWidgetMove(widgetId: string, x: number, y: number) {
    if (!activeDashboardId.value || !socketStore.socket) return;

    const person = signedInPerson();
    socketStore.socket.emit('dashboard:widget:move', {
      dashboardId: activeDashboardId.value,
      widgetId: widgetId,
      positionX: x,
      positionY: y,
      id: person.id,
    });
  }

  function broadcastWidgetResize(widgetId: string, colSpan: number, rowSpan: number) {
    if (!activeDashboardId.value || !socketStore.socket) return;

    const person = signedInPerson();
    socketStore.socket.emit('dashboard:widget:resize', {
      dashboardId: activeDashboardId.value,
      widgetId: widgetId,
      colSpan: colSpan,
      rowSpan: rowSpan,
      id: person.id,
    });
  }

  function broadcastWidgetUpdate(widgetId: string, widgetConfig: JsonValue) {
    if (!activeDashboardId.value || !socketStore.socket) return;

    const person = signedInPerson();
    socketStore.socket.emit('dashboard:widget:update', {
      dashboardId: activeDashboardId.value,
      widgetId: widgetId,
      widgetConfig: widgetConfig,
      id: person.id,
    });
  }

  function broadcastWidgetDelete(widgetId: string) {
    if (!activeDashboardId.value || !socketStore.socket) return;

    const person = signedInPerson();
    socketStore.socket.emit('dashboard:widget:delete', {
      dashboardId: activeDashboardId.value,
      widgetId: widgetId,
      id: person.id,
    });
  }

  function broadcastLayoutChange(dashboardId: string, layoutType: string, layoutConfig: JsonValue | undefined) {
    if (!socketStore.socket) return;

    const person = signedInPerson();
    socketStore.socket.emit('dashboard:layout:change', {
      dashboardId: dashboardId,
      layoutType: layoutType,
      layoutConfig: layoutConfig,
      id: person.id,
    });
  }

  // Setup WebSocket listeners
  function setupCollaborationListeners() {
    if (!socketStore.socket) return;

    socketStore.socket.on('dashboard:user:joined', (data: { id?: string; user_id?: string }) => {
      // The hub relays this one re-mapped to `{ user_id, username }` (its own
      // relay contract, filed separately) while this app sends `id`, so the log
      // reads whichever arrived. Nothing here names anybody: the joined toast
      // was never built, and the editor badges below carry the person's own name
      // and address — never the derived handle (card `t_33ae96b2`).
      console.log(`User ${data.user_id ?? data.id ?? ''} joined dashboard`);
      // Show toast notification
    });

    socketStore.socket.on('dashboard:user:left', (data: { id: string }) => {
      // Remove active editors for this user
      activeEditors.value.forEach((editors, widgetId) => {
        const filtered = editors.filter((e) => e.id !== data.id);
        if (filtered.length > 0) {
          activeEditors.value.set(widgetId, filtered);
        } else {
          activeEditors.value.delete(widgetId);
        }
      });
    });

    socketStore.socket.on(
      'dashboard:widget:editing:start',
      (data: { widgetId: string; id: string; fullName?: string | null; email?: string | null }) => {
        const editors = activeEditors.value.get(data.widgetId) || [];
        editors.push({
          id: data.id,
          fullName: data.fullName ?? null,
          email: data.email ?? null,
          widgetId: data.widgetId,
          timestamp: Date.now(),
        });
        activeEditors.value.set(data.widgetId, editors);
      },
    );

    socketStore.socket.on('dashboard:widget:editing:stop', (data: { widgetId: string; id: string }) => {
      const editors = activeEditors.value.get(data.widgetId) || [];
      const filtered = editors.filter((e) => e.id !== data.id);
      if (filtered.length > 0) {
        activeEditors.value.set(data.widgetId, filtered);
      } else {
        activeEditors.value.delete(data.widgetId);
      }
    });

    socketStore.socket.on(
      'dashboard:widget:move',
      (data: { widgetId: string; positionX: number; positionY: number }) => {
        const widget = widgets.value.find((w) => w.id === data.widgetId);
        if (widget) {
          widget.positionX = data.positionX;
          widget.positionY = data.positionY;
        }
      },
    );

    socketStore.socket.on('dashboard:widget:resize', (data: { widgetId: string; colSpan: number; rowSpan: number }) => {
      const widget = widgets.value.find((w) => w.id === data.widgetId);
      if (widget) {
        widget.colSpan = data.colSpan;
        widget.rowSpan = data.rowSpan;
      }
    });

    socketStore.socket.on(
      'dashboard:widget:update',
      (data: { widgetId: string; widgetConfig: DashboardWidgetConfig }) => {
        const widget = widgets.value.find((w) => w.id === data.widgetId);
        if (widget) {
          widget.widgetConfig = widgetConfigOf(data.widgetConfig);
        }
      },
    );

    socketStore.socket.on('dashboard:widget:delete', (data: { widgetId: string }) => {
      widgets.value = widgets.value.filter((w) => w.id !== data.widgetId);
    });

    socketStore.socket.on(
      'dashboard:layout:change',
      (data: { layoutType: string; layoutConfig: DashboardLayoutConfig }) => {
        if (activeDashboard.value) {
          activeDashboard.value.layoutType = data.layoutType;
          activeDashboard.value.layoutConfig = data.layoutConfig;
        }
      },
    );
  }

  /**
   * Get zoom state for a dashboard
   */
  function getZoomState(dashboardId: string) {
    return zoomStates.value.get(dashboardId) || null;
  }

  /**
   * Set zoom state for a dashboard
   */
  function setZoomState(dashboardId: string, state: { scale: number; offsetX: number; offsetY: number }) {
    zoomStates.value.set(dashboardId, state);
  }

  function resetForHubSwitch() {
    dashboards.value = [];
    activeDashboardId.value = null;
    widgets.value = [];
    editMode.value = false;
    loading.value = false;
    error.value = null;
    activeEditors.value = new Map();
    dashboardData.value = {
      datapoints: new Map(),
    };
    dataLoaded.value = false;
    visibleWidgets.value = new Set();
    subscribedDatapoints.value = new Set();
    zoomStates.value = new Map();
    // The 3D model is the hub's: its row, its poll and its parsed scene must not
    // outlive the connection that served them (`t_hub_bim_model`).
    useBimModelStore().reset();
    disposeAllBimScenes();
  }

  return {
    // State
    dashboards,
    activeDashboardId,
    activeDashboard,
    layouts,
    systemPages,
    modelPage,
    mobileDefaultDashboard,
    heroLayoutsFor,
    customDashboards,
    hasHubBimDashboard,
    pageNavigationDashboards,
    bottomMenuDashboards,
    widgets,
    sortedWidgets,
    editMode,
    loading,
    error,
    canEdit,
    isMobile,
    activeEditors,
    dashboardData,
    dataLoaded,

    // Actions
    loadDashboards,
    loadWidgets,
    loadDeviceDatapoints,
    setActiveDashboard,
    createDashboard,
    updateDashboard,
    deleteDashboard,
    reorderDashboards,
    addWidget,
    updateWidget,
    moveWidget,
    resizeWidget,
    deleteWidget,
    toggleEditMode,
    markWidgetVisible,
    markWidgetHidden,
    setupCollaborationListeners,
    startEditingWidget,
    stopEditingWidget,
    getZoomState,
    setZoomState,
    resetForHubSwitch,
  };
});
